import React from 'react';
import { X, CheckCircle2, XCircle, Loader2, Minimize2, Square, Play } from 'lucide-react';

interface PlanTask {
  lessonId: number;
  lessonTitle: string;
  artifactId: number | null;
  status: 'pending' | 'creating' | 'running' | 'stopped' | 'done' | 'error';
  currentStage: string;
  currentStep: string;
  progress: number;
  error?: string;
}

interface ProgressMonitorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onMinimize: () => void;
  tasks: Map<number, PlanTask>;
  onStop: (lessonId: number) => void;
  onResume: (lessonId: number) => void;
}

const ProgressMonitorModal: React.FC<ProgressMonitorModalProps> = ({
  isOpen,
  onClose,
  onMinimize,
  tasks,
  onStop,
  onResume
}) => {
  if (!isOpen) return null;

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <div className="w-5 h-5 rounded-full border-2 border-gray-300" />;
      case 'creating':
        return <Loader2 size={20} className="text-blue-500 animate-spin" />;
      case 'running':
        return <Loader2 size={20} className="text-purple-500 animate-spin" />;
      case 'stopped':
        return <Square size={20} className="text-orange-500" />;
      case 'done':
        return <CheckCircle2 size={20} className="text-green-500" />;
      case 'error':
        return <XCircle size={20} className="text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return '等待中';
      case 'creating': return '创建教案...';
      case 'running': return '生成中';
      case 'stopped': return '已停止';
      case 'done': return '完成';
      case 'error': return '失败';
      default: return status;
    }
  };

  const taskArray = Array.from(tasks.values());
  const completedCount = taskArray.filter(t => t.status === 'done').length;
  const errorCount = taskArray.filter(t => t.status === 'error').length;
  const totalCount = taskArray.length;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-purple-50 to-pink-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg">
              <Loader2 size={20} className="text-white animate-spin" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">批量生成进度监控</h2>
              <p className="text-sm text-gray-500">
                已完成 {completedCount}/{totalCount} {errorCount > 0 && `· 失败 ${errorCount}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onMinimize}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="最小化"
            >
              <Minimize2 size={20} className="text-gray-500" />
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X size={20} className="text-gray-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-3">
            {taskArray.map(task => (
              <div
                key={task.lessonId}
                className="border rounded-lg p-4 hover:shadow-sm transition-shadow"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    {getStatusIcon(task.status)}
                    <span className="font-medium text-gray-900">{task.lessonTitle}</span>
                    {task.artifactId && (
                      <span className="text-xs text-gray-400">ID: {task.artifactId}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">{getStatusText(task.status)}</span>
                    {task.status === 'running' && (
                      <button
                        onClick={() => onStop(task.lessonId)}
                        className="px-3 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 transition-colors flex items-center gap-1"
                        title="停止生成"
                      >
                        <Square size={12} />
                        停止
                      </button>
                    )}
                    {task.status === 'stopped' && (
                      <button
                        onClick={() => onResume(task.lessonId)}
                        className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors flex items-center gap-1"
                        title="继续生成"
                      >
                        <Play size={12} />
                        继续
                      </button>
                    )}
                  </div>
                </div>
                
                {task.status === 'running' && (
                  <div className="mt-3">
                    <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
                      <span>{task.currentStage} - {task.currentStep}</span>
                      <span className="font-medium">{task.progress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                      <div
                        className="bg-gradient-to-r from-purple-500 to-pink-500 h-2.5 rounded-full transition-all duration-300"
                        style={{ width: `${task.progress}%` }}
                      />
                    </div>
                  </div>
                )}
                
                {task.status === 'done' && (
                  <div className="mt-2 text-sm text-green-600 flex items-center gap-1">
                    <CheckCircle2 size={14} />
                    教案生成完成
                  </div>
                )}
                
                {task.status === 'error' && (
                  <div className="mt-2 text-sm text-red-600 flex items-center gap-1">
                    <XCircle size={14} />
                    {task.error || '生成失败'}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t bg-gray-50">
          <div className="text-sm text-gray-500">
            {completedCount === totalCount ? (
              <span className="text-green-600 font-medium">✓ 所有任务已完成</span>
            ) : (
              <span>正在生成中，请勿关闭此窗口...</span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {completedCount === totalCount && (
              <button
                onClick={onClose}
                className="px-6 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all"
              >
                完成
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgressMonitorModal;
